import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import AuthToggle from './components/AuthToggle';
import LoginForm from './components/LoginForm';
import RegisterForm from './components/RegisterForm';
import TrustBadges from './components/TrustBadges';
import { useAuth } from '../../contexts/AuthContext';

const Authentication = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, profile, loading: authLoading } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const source = searchParams.get('source');
  
  const mode = searchParams.get('mode');
  const email = searchParams.get('email');
  const name = searchParams.get('name');
  const whopToken = searchParams.get('whop_token');
  const isNew = searchParams.get('new') === 'true';
  
  // If email is provided, default to register mode
  useEffect(() => {
    if ((email || whopToken) && mode === 'register') {
      setIsLogin(false);
    }
  }, [email, whopToken, mode]);

  // Block free registration - only allow Whop registrations
  if (mode === 'register' && !whopToken && !email) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-card rounded-2xl shadow-xl p-8 border border-border">
            <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="Lock" size={32} color="var(--color-destructive)" />
            </div>
            
            <h2 className="text-2xl font-bold text-foreground mb-3">
              Registration Locked
            </h2>
            
            <p className="text-muted-foreground mb-6">
              Free registration is no longer available. To access PitchLab, you must first purchase a Pro, Elite, or Founder subscription through our checkout.
            </p>

            <button
              onClick={() => navigate('/app/subscription')}
              className="w-full px-6 py-3 bg-primary text-white rounded-lg hover:opacity-90 transition-opacity font-semibold mb-3"
            >
              View Plans & Subscribe
            </button>

            <button
              onClick={() => {
                setIsLogin(true);
              }}
              className="w-full px-6 py-3 bg-card border border-border text-foreground rounded-lg hover:bg-muted transition-colors"
            >
              Sign In to Existing Account
            </button>

            <p className="text-xs text-muted-foreground mt-4">
              Questions? Contact us at support@earnmyedge.com
            </p>
          </div>
        </div>
      </div>
    );
  }

  useEffect(() => {
    if (!authLoading && user && profile) {
      // Get tenant from query params if present
      const tenantParam = searchParams.get('tenant');
      const tenantQuery = tenantParam ? `?tenant=${encodeURIComponent(tenantParam)}` : '';
      
      // Check if user has completed onboarding
      if (!profile.onboarding_completed || !profile.industry) {
        navigate(`/app/industry-selection${tenantQuery}`);
      } else {
        navigate(`/app/dashboard${tenantQuery}`);
      }
    }
  }, [user, profile, authLoading, navigate, searchParams]);

  const isUGC = useMemo(() => source === 'ugc' || source === 'ugc-subscription', [source]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Whop Purchase Pending Mode
  if (mode === 'whop-pending') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-card rounded-2xl shadow-xl p-8 border border-border">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="CheckCircle" size={32} color="var(--color-primary)" />
            </div>
            
            <h2 className="text-2xl font-bold text-foreground mb-3">
              Purchase Complete!
            </h2>
            
            <p className="text-muted-foreground mb-6">
              Your payment was successful. We're setting up your account right now.
            </p>

            <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-foreground font-medium mb-2">What happens next?</p>
              <ol className="text-sm text-muted-foreground space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-primary font-bold">1.</span>
                  <span>Check your email inbox (might take 1-2 minutes)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary font-bold">2.</span>
                  <span>Click the magic link to activate your account</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary font-bold">3.</span>
                  <span>Start using your premium features immediately</span>
                </li>
              </ol>
            </div>

            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                💡 <strong>Tip:</strong> Add noreply@earnmyedge.com to your contacts so our emails don't go to spam!
              </p>
            </div>

            <button
              onClick={() => navigate('/')}
              className="w-full px-6 py-3 bg-card border border-border text-foreground rounded-lg hover:bg-muted transition-colors"
            >
              Return to Home
            </button>

            <p className="text-xs text-muted-foreground mt-4">
              Having issues? Contact us at support@earnmyedge.com
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Check Email Mode (after Whop purchase)
  if (mode === 'check-email') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-card rounded-2xl shadow-xl p-8 border border-border">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="Mail" size={32} color="var(--color-primary)" />
            </div>
            
            <h2 className="text-2xl font-bold text-foreground mb-3">
              {isNew ? 'Check Your Email!' : 'Welcome Back!'}
            </h2>
            
            <p className="text-muted-foreground mb-6">
              {isNew ? (
                <>
                  We've sent a magic link to <strong className="text-foreground">{email}</strong> to complete your account setup.
                </>
              ) : (
                <>
                  We've sent a sign-in link to <strong className="text-foreground">{email}</strong>
                </>
              )}
            </p>

            <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-foreground font-medium mb-2">What's next?</p>
              <ol className="text-sm text-muted-foreground space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-primary font-bold">1.</span>
                  <span>Check your email inbox (and spam folder)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary font-bold">2.</span>
                  <span>Click the magic link in the email</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary font-bold">3.</span>
                  <span>{isNew ? 'Complete your profile setup' : 'Access your upgraded features'}</span>
                </li>
              </ol>
            </div>

            <button
              onClick={() => navigate('/')}
              className="w-full px-6 py-3 bg-card border border-border text-foreground rounded-lg hover:bg-muted transition-colors"
            >
              Return to Home
            </button>

            <p className="text-xs text-muted-foreground mt-4">
              Didn't receive the email? Check your spam folder or contact support.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #5b21b6 0%, #3b82f6 100%)' }}>
      <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        {/* Left Side - Branding & Features */}
        <div className="hidden lg:flex flex-col justify-center space-y-8">
          {/* Hero Graphic */}
          <div className="flex justify-center mb-4">
            <Image
              src="/assets/landing-graphic.png"
              alt="Sales success visualization with growth chart and trophy"
              className="w-64 h-auto"
            />
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #5b21b6 0%, #3b82f6 100%)' }}>
                <Icon name="Zap" size={28} color="#FFFFFF" />
              </div>
              <h1 className="text-3xl font-bold text-white">Earn My Edge</h1>
            </div>
            <p className="text-xl text-white/90">
              Master your sales pitch with AI-powered coaching in just 5-10 minutes daily
            </p>
          </div>

          <div className="space-y-6">
            {[
            {
              icon: 'Target',
              title: 'Daily Micro-Drills',
              description: 'Practice real scenarios with instant AI feedback on clarity, confidence, and closing techniques'
            },
            {
              icon: 'Shield',
              title: 'Objection Mastery',
              description: 'Build confidence handling tough objections with industry-specific practice sessions'
            },
            {
              icon: 'FileText',
              title: 'Smart Script Generation',
              description: 'Get personalized SMS, email, and call scripts tailored to your lead situations'
            },
            {
              icon: 'TrendingUp',
              title: 'Track Your Progress',
              description: 'Monitor skill improvements and maintain your daily training streak'
            }]?.
            map((feature, index) =>
            <div key={index} className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                  <Icon name={feature?.icon} size={20} color="#FFFFFF" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-white mb-1">{feature?.title}</h3>
                  <p className="text-sm text-white/80">{feature?.description}</p>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-4 pt-4">
            <div className="flex -space-x-3">
              {[
              { src: "https://img.rocket.new/generatedImages/rocket_gen_img_1d9e9bf53-1763296643653.png", alt: 'Professional headshot of Hispanic male sales representative with short black hair wearing navy suit' },
              { src: "https://img.rocket.new/generatedImages/rocket_gen_img_15dba332b-1763294945056.png", alt: 'Professional headshot of Asian female sales professional with long dark hair in business attire' },
              { src: "https://img.rocket.new/generatedImages/rocket_gen_img_18debffcc-1763296321866.png", alt: 'Professional headshot of Caucasian male sales manager with brown hair in formal business suit' },
              { src: "https://img.rocket.new/generatedImages/rocket_gen_img_1436c4089-1763294954354.png", alt: 'Professional headshot of African American female sales executive with curly hair in professional outfit' }]?.
              map((user, index) =>
              <div key={index} className="w-10 h-10 rounded-full border-2 border-white/30 overflow-hidden">
                  <Image
                  src={user?.src}
                  alt={user?.alt}
                  className="w-full h-full object-cover" />

                </div>
              )}
            </div>
            <div>
              <p className="text-sm font-semibold text-white">Join 2,500+ sales professionals</p>
              <p className="text-xs text-white/80">Improving their pitch daily</p>
            </div>
          </div>
        </div>

        {/* Right Side - Authentication Form */}
        <div className="w-full">
          <div className="bg-card rounded-2xl shadow-elevated p-8 space-y-6">
            {/* Mobile Logo */}
            <div className="lg:hidden flex items-center justify-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #5b21b6 0%, #3b82f6 100%)' }}>
                <Icon name="Zap" size={24} color="#FFFFFF" />
              </div>
              <h1 className="text-2xl font-bold text-foreground">Earn My Edge</h1>
            </div>

            <div className="text-center space-y-2">
              <h2 className="text-2xl font-bold text-foreground">
                {isLogin ? 'Welcome Back' : 'Start Your Journey'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {isLogin ?
                'Sign in to continue your coaching progress' : 'Create your account and master your sales pitch'}
              </p>
              {isUGC && (
                <div className="mt-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                  <div className="flex items-center gap-2 font-semibold text-amber-900 justify-center">
                    <Icon name="Video" size={14} /> UGC Platform
                  </div>
                  <p className="mt-1">
                    You’re signing in for the UGC Creator platform. Use your Creator/Creator Elite/Founder account to continue.
                  </p>
                </div>
              )}
            </div>

            <AuthToggle isLogin={isLogin} onToggle={setIsLogin} allowRegister={!!whopToken || !!email} />

            {/* Email Forms */}
            {isLogin ? (
              <LoginForm />
            ) : (
              <RegisterForm 
                prefillEmail={email} 
                prefillName={name}
                whopToken={whopToken}
              />
            )}

            <TrustBadges />
          </div>

          {/* Mobile Features Preview */}
          <div className="lg:hidden mt-8 space-y-4">
            <p className="text-center text-sm font-semibold text-white">
              Trusted by 2,500+ sales professionals
            </p>
            <div className="grid grid-cols-2 gap-3">
              {[
              { icon: 'Target', label: 'Daily Drills' },
              { icon: 'Shield', label: 'Objection Practice' },
              { icon: 'FileText', label: 'Smart Scripts' },
              { icon: 'TrendingUp', label: 'Progress Tracking' }]?.
              map((feature, index) =>
              <div key={index} className="flex items-center gap-2 p-3 bg-white/20 backdrop-blur-sm rounded-lg">
                  <Icon name={feature?.icon} size={18} color="#FFFFFF" />
                  <span className="text-xs font-medium text-white">{feature?.label}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>);

};

export default Authentication;
